import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AdminLayout from "../../components/layouts/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { 
  Plus, Store, MapPin, Phone, Mail, Trash2, ExternalLink, Copy, CheckCircle, 
  AlertCircle, Eye, EyeOff, Settings, ChevronRight
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "../../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RestaurantsList = () => {
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createdUsers, setCreatedUsers] = useState(null);
  const [showPasswords, setShowPasswords] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    category: "restaurant",
    address: "",
    city: "",
    country: "Kenya",
    contact_phone: "",
    contact_email: "",
    currency: "KES",
    timezone: "Africa/Nairobi",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchRestaurants();
  }, []);

  const fetchRestaurants = async () => {
    try {
      const response = await axios.get(`${API}/admin/restaurants`);
      setRestaurants(response.data);
    } catch (error) {
      console.error("Failed to fetch restaurants:", error);
      toast.error("Failed to load restaurants");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/admin/restaurants`, formData);
      setRestaurants([...restaurants, response.data]);
      setCreatedUsers(response.data.created_users);
      toast.success("Restaurant created successfully!");
      setFormData({
        name: "",
        category: "restaurant",
        address: "",
        city: "",
        country: "Kenya",
        contact_phone: "",
        contact_email: "",
        currency: "KES",
        timezone: "Africa/Nairobi",
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create restaurant");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this restaurant? This action cannot be undone.")) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/restaurants/${id}`);
      setRestaurants(restaurants.filter((r) => r.id !== id));
      toast.success("Restaurant deleted");
    } catch (error) {
      toast.error("Failed to delete restaurant");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const togglePasswordVisibility = (role) => {
    setShowPasswords((prev) => ({ ...prev, [role]: !prev[role] }));
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="restaurants-list">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Restaurants</h1>
            <p className="text-muted-foreground">Manage all restaurants on the platform</p>
          </div>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="create-restaurant-btn">
                <Plus className="w-4 h-4" />
                Add Restaurant
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Restaurant</DialogTitle>
              </DialogHeader>
              
              {createdUsers ? (
                <div className="space-y-4">
                  <Alert className="bg-green-50 border-green-200">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <AlertTitle className="text-green-800">Restaurant Created!</AlertTitle>
                    <AlertDescription className="text-green-700">
                      Save these credentials securely. Users must change their password on first login.
                    </AlertDescription>
                  </Alert>
                  
                  <div className="space-y-3">
                    {createdUsers.map((user, idx) => (
                      <Card key={idx} className="border border-border">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-2">
                            <Badge variant="outline" className="capitalize">
                              {user.role.replace("_", " ")}
                            </Badge>
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">Email:</span>
                              <div className="flex items-center gap-2">
                                <code className="bg-muted px-2 py-0.5 rounded text-xs">{user.email}</code>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-6 w-6"
                                  onClick={() => copyToClipboard(user.email)}
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">Password:</span>
                              <div className="flex items-center gap-2">
                                <code className="bg-muted px-2 py-0.5 rounded text-xs">
                                  {showPasswords[user.role] ? user.password : "••••••••"}
                                </code>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-6 w-6"
                                  onClick={() => togglePasswordVisibility(user.role)}
                                >
                                  {showPasswords[user.role] ? (
                                    <EyeOff className="h-3 w-3" />
                                  ) : (
                                    <Eye className="h-3 w-3" />
                                  )}
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-6 w-6"
                                  onClick={() => copyToClipboard(user.password)}
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  
                  <Button 
                    className="w-full" 
                    onClick={() => { setCreatedUsers(null); setIsCreateOpen(false); }}
                  >
                    Done
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleCreate} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 space-y-2">
                      <Label>Restaurant Name *</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g., Java House"
                        required
                        data-testid="restaurant-name-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Category *</Label>
                      <Select
                        value={formData.category}
                        onValueChange={(v) => setFormData({ ...formData, category: v })}
                      >
                        <SelectTrigger data-testid="restaurant-category-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="restaurant">Restaurant</SelectItem>
                          <SelectItem value="cafe">Café</SelectItem>
                          <SelectItem value="fast_food">Fast Food</SelectItem>
                          <SelectItem value="takeaway">Takeaway</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>City *</Label>
                      <Input
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        placeholder="Nairobi"
                        required
                        data-testid="restaurant-city-input"
                      />
                    </div>
                    
                    <div className="col-span-2 space-y-2">
                      <Label>Address *</Label>
                      <Input
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        placeholder="123 Kenyatta Avenue"
                        required
                        data-testid="restaurant-address-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Phone *</Label>
                      <Input
                        value={formData.contact_phone}
                        onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                        placeholder="+254 700 000 000"
                        required
                        data-testid="restaurant-phone-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                        placeholder="contact@restaurant.com"
                        required
                        data-testid="restaurant-email-input"
                      />
                    </div>
                  </div>
                  
                  <Alert className="bg-blue-50 border-blue-200">
                    <AlertCircle className="h-4 w-4 text-blue-600" />
                    <AlertDescription className="text-blue-700 text-sm">
                      Login credentials for Owner, Kitchen, and Cashier roles will be auto-generated.
                    </AlertDescription>
                  </Alert>
                  
                  <Button type="submit" className="w-full" disabled={submitting} data-testid="submit-restaurant-btn">
                    {submitting ? "Creating..." : "Create Restaurant"}
                  </Button>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </div>

        {/* Restaurants List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : restaurants.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Store className="w-12 h-12 text-muted-foreground/50 mb-4" />
              <h3 className="font-semibold text-lg mb-1">No restaurants yet</h3>
              <p className="text-muted-foreground text-sm mb-4">Get started by adding your first restaurant</p>
              <Button onClick={() => setIsCreateOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Restaurant
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {restaurants.map((restaurant) => {
              const statusColors = {
                active: "bg-green-100 text-green-700",
                inactive: "bg-gray-100 text-gray-700",
                suspended: "bg-red-100 text-red-700",
              };
              const subscriptionColors = {
                trial: "bg-blue-100 text-blue-700",
                active: "bg-green-100 text-green-700",
                expired: "bg-amber-100 text-amber-700",
                suspended: "bg-red-100 text-red-700",
              };
              
              return (
                <Card 
                  key={restaurant.id} 
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/admin/restaurants/${restaurant.id}`)}
                >
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Store className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="font-semibold text-lg">{restaurant.name}</h3>
                            <Badge variant="outline" className="capitalize text-xs">
                              {restaurant.category?.replace("_", " ")}
                            </Badge>
                            <Badge className={cn("text-xs", statusColors[restaurant.status] || statusColors.active)}>
                              {restaurant.status || "active"}
                            </Badge>
                            <Badge className={cn("text-xs", subscriptionColors[restaurant.subscription_status] || subscriptionColors.trial)}>
                              {restaurant.subscription_status || "trial"}
                            </Badge>
                          </div>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {restaurant.city}
                            </span>
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {restaurant.contact_phone}
                            </span>
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {restaurant.contact_email}
                            </span>
                          </div>
                          <div className="mt-2">
                            <code className="text-xs bg-muted px-2 py-1 rounded">
                              {restaurant.slug}.dalabsom.com
                            </code>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); window.open(`/r/${restaurant.slug}`, '_blank'); }}
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          Preview
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); navigate(`/admin/restaurants/${restaurant.id}`); }}
                        >
                          <Settings className="w-4 h-4 mr-1" />
                          Manage
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleDelete(restaurant.id); }}
                          data-testid={`delete-restaurant-${restaurant.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default RestaurantsList;
